import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Check } from "lucide-react";
import api, { eur, formatApiError } from "@/lib/api";
import { StockBadge } from "@/components/ui-bits";

const EMPTY = { slug: "", brand: "axe", model: "", ml: "150 ml", tagline: "", description: "", image_url: "", image_style: "promo", price: 2.49, discount_price: "", stock: 0, active: true, order: 0 };

const InlineCell = ({ value, onSave, testid, placeholder = "" }) => {
  const [val, setVal] = useState(value === null || value === undefined ? "" : String(value));
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setVal(value === null || value === undefined ? "" : String(value));
  }, [value]);

  const commit = async () => {
    if (val === String(value ?? "")) return;
    await onSave(val);
    setSaved(true);
    setTimeout(() => setSaved(false), 1200);
  };

  return (
    <span className="relative inline-flex items-center">
      <input
        data-testid={testid}
        value={val}
        placeholder={placeholder}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.target.blur();
          if (e.key === "Escape") setVal(value === null || value === undefined ? "" : String(value));
        }}
        className="w-20 bg-obsidian border border-gold/15 px-2.5 py-1.5 text-xs text-cream focus:outline-none focus:border-gold transition-colors"
      />
      {saved && <Check size={12} className="absolute -right-4 text-emerald-400" />}
    </span>
  );
};

export const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");

  const load = () => {
    api.get("/admin/products").then((d) => setProducts(d.data)).catch(() => {});
    api.get("/brands").then((d) => setBrands(d.data)).catch(() => {});
  };
  useEffect(load, []);

  const quickSave = async (slug, payload) => {
    setError("");
    try {
      await api.patch(`/admin/products/${slug}/quick`, payload);
      const d = await api.get("/admin/products");
      setProducts(d.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const openNew = () => { setForm({ ...EMPTY }); setEditing("nieuw"); setError(""); };
  const openEdit = (p) => {
    setForm({
      slug: p.slug, brand: p.brand, model: p.model, ml: p.ml, tagline: p.tagline || "",
      description: p.description || "", image_url: p.image_url, image_style: p.image_style || "atelier",
      price: p.price, discount_price: p.discount_price ?? "", stock: p.stock, active: p.active !== false, order: p.order || 0,
    });
    setEditing(p.slug);
    setError("");
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      discount_price: form.discount_price === "" || form.discount_price === null ? null : parseFloat(form.discount_price),
      stock: parseInt(form.stock) || 0,
      order: parseInt(form.order) || 0,
    };
    try {
      if (editing === "nieuw") {
        await api.post("/admin/products", payload);
      } else {
        await api.put(`/admin/products/${editing}`, payload);
      }
      setEditing(null);
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Product "${slug}" echt verwijderen?`)) return;
    await api.delete(`/admin/products/${slug}`);
    load();
  };

  const F = (key, label, testid, type = "text", extra = {}) => (
    <label className="block">
      <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-smoke block mb-1.5">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full bg-obsidian border border-gold/15 px-3 py-2.5 text-sm text-cream focus:outline-none focus:border-gold/50"
        {...extra}
      />
    </label>
  );

  const shown = products.filter((p) => !filter || p.brand === filter);

  return (
    <div data-testid="admin-products">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
        <h1 className="font-serif font-light text-cream text-3xl">Producten <span className="text-smoke text-lg">({products.length})</span></h1>
        <div className="flex gap-3">
          <select data-testid="admin-brand-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-ink border border-gold/15 px-4 py-2.5 text-xs text-cream">
            <option value="">Alle merken</option>
            {brands.map((b) => <option key={b.slug} value={b.slug}>{b.name}</option>)}
          </select>
          <button data-testid="admin-new-product" onClick={openNew} className="flex items-center gap-2 bg-gold text-obsidian font-mono text-[10px] tracking-[0.25em] uppercase px-5 py-2.5 hover:bg-gold-light transition-colors">
            <Plus size={13} /> Nieuw product
          </button>
        </div>
      </div>
      <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-smoke mb-6">Prijs, korting en voorraad zijn direct aanpasbaar — klik in een cel, wijzig en druk op Enter</p>
      {error && <p data-testid="admin-quick-error" className="text-xs text-red-400 mb-4">{error}</p>}

      <div className="border border-gold/15 bg-ink overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="border-b border-gold/15 text-left font-mono text-[9px] tracking-[0.2em] uppercase text-smoke">
              <th className="p-4">Product</th><th className="p-4">Merk</th><th className="p-4">Prijs €</th><th className="p-4">Korting € (leeg = geen)</th><th className="p-4">Voorraad</th><th className="p-4">Effectief</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((p) => (
              <tr key={p.slug} className="border-b border-gold/5 hover:bg-gold/[0.02]">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-12 bg-obsidian shrink-0 border border-gold/10"><img src={p.image_url} alt="" className="w-full h-full object-contain p-0.5" /></div>
                    <div>
                      <p className="text-cream">{p.model}</p>
                      <p className="font-mono text-[9px] text-smoke">{p.slug} · {p.ml}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4 font-mono text-[10px] uppercase text-mist">{p.brand}</td>
                <td className="p-4">
                  <InlineCell
                    testid={`inline-price-${p.slug}`}
                    value={p.price}
                    onSave={(v) => quickSave(p.slug, { price: parseFloat(v) || 0 })}
                  />
                </td>
                <td className="p-4">
                  <InlineCell
                    testid={`inline-discount-${p.slug}`}
                    value={p.discount_price}
                    placeholder="—"
                    onSave={(v) => quickSave(p.slug, { discount_price: v === "" ? null : parseFloat(v) || 0 })}
                  />
                </td>
                <td className="p-4">
                  <InlineCell
                    testid={`inline-stock-${p.slug}`}
                    value={p.stock}
                    onSave={(v) => quickSave(p.slug, { stock: parseInt(v) || 0 })}
                  />
                </td>
                <td className="p-4">
                  <span className="font-serif text-gold">{p.price > 0 ? eur(p.discount_price || p.price) : "—"}</span>
                  <div className="mt-1"><StockBadge stock={p.stock} /></div>
                </td>
                <td className="p-4">
                  <div className="flex gap-2 justify-end">
                    <button data-testid={`admin-edit-${p.slug}`} onClick={() => openEdit(p)} className="text-mist hover:text-gold transition-colors" aria-label="Bewerken"><Pencil size={14} /></button>
                    <button data-testid={`admin-delete-${p.slug}`} onClick={() => remove(p.slug)} className="text-mist hover:text-red-400 transition-colors" aria-label="Verwijderen"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[160] flex items-center justify-center p-4 bg-obsidian/85 backdrop-blur-sm" data-testid="admin-product-modal">
          <form onSubmit={save} className="w-[min(760px,95vw)] max-h-[88vh] overflow-y-auto bg-elevate border border-gold/25 p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-cream">{editing === "nieuw" ? "Nieuw product" : `Bewerk: ${editing}`}</h2>
              <button type="button" onClick={() => setEditing(null)} aria-label="Sluiten" className="text-mist hover:text-cream"><X size={18} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {F("model", "Modelnaam", "admin-model-input")}
              <label className="block">
                <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-smoke block mb-1.5">Merk</span>
                <select data-testid="admin-brand-select" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="w-full bg-obsidian border border-gold/15 px-3 py-2.5 text-sm text-cream">
                  {brands.map((b) => <option key={b.slug} value={b.slug}>{b.name}</option>)}
                </select>
              </label>
              {F("ml", "Inhoud (ml)", "admin-ml-input")}
              {F("order", "Sorteervolgorde", "admin-order-input", "number")}
              {F("price", "Prijs (€, 0 = geen prijs)", "admin-price-input", "number", { step: "0.01", min: "0" })}
              {F("discount_price", "Kortingprijs (€, leeg = geen)", "admin-discount-input", "number", { step: "0.01", min: "0" })}
              {F("stock", "Voorraad", "admin-stock-input", "number", { min: "0" })}
              <label className="block">
                <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-smoke block mb-1.5">Afbeeldingstijl</span>
                <select data-testid="admin-style-select" value={form.image_style} onChange={(e) => setForm({ ...form, image_style: e.target.value })} className="w-full bg-obsidian border border-gold/15 px-3 py-2.5 text-sm text-cream">
                  <option value="promo">promo (koyu, volledig zichtbaar)</option>
                  <option value="studio">studio (wit papier)</option>
                  <option value="atelier">atelier (cinematisch)</option>
                </select>
              </label>
              <div className="sm:col-span-2">{F("image_url", "Afbeelding-URL of pad (/products/...)", "admin-image-input")}</div>
              <div className="sm:col-span-2">{F("tagline", "Korte slogan", "admin-tagline-input")}</div>
              <label className="block sm:col-span-2">
                <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-smoke block mb-1.5">Omschrijving</span>
                <textarea data-testid="admin-description-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-obsidian border border-gold/15 px-3 py-2.5 text-sm text-cream focus:outline-none focus:border-gold/50" />
              </label>
              <label className="flex items-center gap-2.5 sm:col-span-2">
                <input data-testid="admin-active-toggle" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-gold" />
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-mist">Zichtbaar in de winkel</span>
              </label>
            </div>
            {error && <p data-testid="admin-product-error" className="mt-4 text-xs text-red-400">{error}</p>}
            <div className="mt-6 flex gap-3">
              <button type="submit" data-testid="admin-product-save" className="bg-gold text-obsidian font-mono text-[10px] tracking-[0.3em] uppercase px-8 py-3.5">Opslaan</button>
              <button type="button" onClick={() => setEditing(null)} className="border border-cream/15 text-mist font-mono text-[10px] tracking-[0.25em] uppercase px-8 py-3.5">Annuleren</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
